import React, { useContext, useState } from 'react';
import { Avatar, Box, IconButton, SvgIcon, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CourseCoverPageContext } from './CourseCoverPageProvider';
import {
  borderRadius,
  cardDimensionsMedia,
  palette,
  spaces,
} from '../../../assets/styles/themes/tokens';
import XIcon from '../../../assets/course/x.svg';
import FacebookIcon from '../../../assets/course/facebook.svg';
import LinkedInIcon from '../../../assets/course/linkedin.svg';
import InstagramIcon from '../../../assets/course/instagram.svg';
import { Author } from '@klab-berlin/api-sdk/lib/types/responses/Author';
import CardDescription from '../../../components/cards/common/CardDescription/NewCardDescription';
import useScreenSize from '../../../utils/useScreenSize';

const CourseCoverPageAuthors = () => {
  const { t } = useTranslation();
  const { isXsScreen } = useScreenSize();
  const { courseMindItem } = useContext(CourseCoverPageContext);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: cardDimensionsMedia.desktop.maxWidth,
      }}
    >
      <Box
        sx={{
          alignItems: isXsScreen ? 'flex-start': 'flex-end',
          display: 'flex',
          gap: isXsScreen ? spaces.xxs : spaces.xs,
          paddingY: spaces.md,
          marginBottom: spaces.md,
          flexDirection: isXsScreen ? 'column' : 'row',
        }}
      >
        <Typography variant='h5' color='secondary'>
          {t('Author:inside')}
        </Typography>
        <Typography variant={isXsScreen ? 'caption' : 'subtitle1'} color='secondary'>
          {t('In cooperation with')}: {courseMindItem.publisher}
        </Typography>
      </Box>
      {courseMindItem.course?.authors &&
        courseMindItem.course.authors.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: spaces.md,
          }}
        >
          {courseMindItem.course.authors.map((author) => (
            <AuthorItem key={author.id} author={author} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const AuthorItem: React.FC<{ author: Author }> = ({ author }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isXsScreen } = useScreenSize();

  return (
    <Box
      sx={{
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: palette.elevation.outlined,
        borderRadius: borderRadius.lg,
        padding: isXsScreen ? spaces.sm : spaces.md,
        display: 'flex',
        gap: spaces.lg,
        flexDirection: isXsScreen ? 'column' : 'row',
        position: 'relative',
      }}
      data-testid='course-cover-author-item'
    >
      <Avatar
        variant='circular'
        sx={{ width: '120px', height: '120px' }}
        src={author.thumbnail}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.sm,
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: isXsScreen ? 'unset' : spaces.xl,
          }}
        >
          <Typography
            variant='h6'
            color='secondary'
            sx={{ height: 'fit-content' }}
          >
            {author.name}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              position: isXsScreen ? 'absolute' : 'unset',
              top: isXsScreen ? spaces.md : 0,
              right: isXsScreen ? spaces.sm : 0,
            }}
          >
            {author.xUrl && <SocialMediaLink url={author.xUrl} icon={XIcon} testId='author-item-x-link' />}
            {author.facebookUrl && (
              <SocialMediaLink url={author.facebookUrl} icon={FacebookIcon} testId='author-item-fb-link' />
            )}
            {author.instagramUrl && (
              <SocialMediaLink url={author.instagramUrl} icon={InstagramIcon} testId='author-item-ig-link'  />
            )}
            {author.linkedinUrl && (
              <SocialMediaLink url={author.linkedinUrl} icon={LinkedInIcon} testId='author-item-ln-link' />
            )}
          </Box>
        </Box>
        {author.description && (
          <CardDescription
            isExpanded={isExpanded}
            onShowToggleClick={() => setIsExpanded(!isExpanded)}
            description={author.description}
          />
        )}
      </Box>
    </Box>
  );
};

const SocialMediaLink: React.FC<{ url: string; icon: React.FC, testId: string}> = ({
  url,
  icon,
  testId,
}) => (
  <IconButton
    href={url}
    target='_blank'
    rel='noreferrer'
    color='secondary'
    sx={{
      padding: spaces.xxs,
      width: spaces.xl,
      height: spaces.xl,
      '&:hover': {
        color: palette.secondary.main,
      },
    }}
    data-testid={testId}
  >
    <SvgIcon component={icon} />
  </IconButton>
);

export default CourseCoverPageAuthors;
